import Cookies from "@/comps/cookies"
import Favicon from "@/comps/favicon"
import "@/styles/globals.css"
import {
	AdjustProvider,
	UsePageViewEvent,
} from "@/utils/adjust"

import useClarity from "@/utils/clarity"
import { GoogleOneTap } from "@/utils/googleapi"
import { NotificationProvider } from "@/utils/notification"
import useUnflash from "@/utils/unflash"

import {
	SSRAuthProvider,
	UserInfo,
} from "@/utils/client-auth"
import { WalletProvider } from "@/utils/wallet"
import "intl-pluralrules"
import { appWithTranslation } from "next-i18next"
import { ThemeProvider } from "next-themes"
import type { AppProps } from "next/app"
import { ReactNode } from "react"
import {
	QueryClient,
	QueryClientProvider,
} from "react-query"
import { createGlobalStyle } from "styled-components"

export const queryClient = new QueryClient()

const GlobalStyle = createGlobalStyle`
	.grecaptcha-badge {
		display: none;
	}
`

interface CustomProps extends AppProps {
	Component: AppProps["Component"] & {
		theme?: string
		Layout?: (props: AppProviderProps) => JSX.Element
	}
}

const MyApp = (props: CustomProps) => {
	const { Component, pageProps } = props
	useUnflash()

	const { theme, Layout } = Component
	const { userInfo } = pageProps as {
		userInfo: UserInfo
	}
	useClarity(userInfo?.userId)

	const Providers = Layout ?? AppProviders

	return (
		<Providers theme={theme} userInfo={userInfo}>
			<Component {...pageProps} />
		</Providers>
	)
}

export interface AppProviderProps {
	theme?: string
	children: ReactNode
	userInfo: UserInfo
}

function AppProviders(props: AppProviderProps) {
	const { children, theme, userInfo } = props

	return (
		<ThemeProvider
			attribute="class"
			forcedTheme={theme}
			defaultTheme="system"
			enableSystem={true}>
			<GlobalStyle />
			<QueryClientProvider client={queryClient}>
				<SSRAuthProvider userInfo={userInfo}>
					<NotificationProvider timeout={3000}>
						<WalletProvider>
							<AdjustProvider>
								<Favicon />
								<Cookies />
								{children}
								<UsePageViewEvent />
								<GoogleOneTap />
							</AdjustProvider>
						</WalletProvider>
					</NotificationProvider>
				</SSRAuthProvider>
			</QueryClientProvider>
		</ThemeProvider>
	)
}

export default appWithTranslation(MyApp)
